import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Home, ClipboardList, ShoppingBag, UserRound, MapPin, ChevronDown, ArrowRight } from "lucide-react";
import ColorThemeSwitcher from "@/components/ColorThemeSwitcher";
import BrandLogo from "@/components/BrandLogo";
import MobileTabBar from "@/components/MobileTabBar";

export default function BuyerLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { count } = useCart();

  const nav = [
    { to: "/app", icon: Home, label: "الرئيسية", exact: true },
    { to: "/app/orders", icon: ClipboardList, label: "طلباتي" },
    { to: "/app/checkout", icon: ShoppingBag, label: "السلة", badge: count },
    { to: "/app/profile", icon: UserRound, label: "حسابي" },
  ];

  const isActive = (item) => item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);
  const userInitial = user?.name?.trim()?.charAt(0) || "ك";
  const defaultAddress = user?.saved_addresses?.find((item) => item.is_default)
    || user?.saved_addresses?.[0];
  const primaryScreen = ["/app", "/app/orders", "/app/profile"].includes(location.pathname);
  const activeTab = location.pathname === "/app" || location.pathname.startsWith("/app/shop") || location.pathname.startsWith("/app/product")
    ? "home"
    : location.pathname === "/app/orders"
      ? "orders"
      : location.pathname === "/app/checkout"
        ? "cart"
        : "profile";
  const pageTitles = {
    "/app/orders": "طلباتي",
    "/app/checkout": "السلة",
    "/app/profile": "حسابي",
  };
  const navigateTab = (id) => navigate({
    home: "/app",
    orders: "/app/orders",
    cart: "/app/checkout",
    profile: "/app/profile",
  }[id]);

  const LocationPill = () => (
    <button
      data-testid="location-pill"
      onClick={() => navigate("/app/profile")}
      className="group flex min-w-0 items-center gap-2 text-start px-2 py-1.5 rounded-2xl hover:bg-muted/40 transition-colors"
      aria-label="تحديد وتعديل موقع التوصيل"
    >
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-brand text-brand-foreground shadow-sm">
        <MapPin className="h-4 w-4" />
      </div>
      <div className="min-w-0 max-w-[140px] sm:max-w-xs">
        <div className="text-[10px] font-medium leading-none text-muted-foreground">التوصيل إلى</div>
        <div className="mt-1 flex items-center gap-1 text-xs sm:text-sm font-bold">
          <span className="truncate">{defaultAddress?.label || defaultAddress?.address || user?.address || "كفرنبل، سوريا"}</span>
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        </div>
      </div>
    </button>
  );

  return (
    <div className={`buyer-page min-h-screen bg-background flex flex-col ${primaryScreen ? "pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-10" : "pb-8"}`} dir="rtl">
      {/* Responsive Top Bar */}
      <header className="safe-top-header glass sticky top-0 z-40 border-b border-brand/10 backdrop-blur-md">
        <div className="mx-auto flex h-14 sm:h-[72px] max-w-7xl items-center justify-between gap-3 px-3 sm:px-6 lg:px-8">
          {/* Mobile Drawer Toggle & Logo */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/app")}
              className="hidden shrink-0 items-center gap-2.5 active:scale-95 transition-transform md:flex"
              data-testid="buyer-logo"
              aria-label="الصفحة الرئيسية لبيب بيب"
            >
              <BrandLogo className="h-11 w-[126px] object-contain" />
            </button>
            {!primaryScreen ? (
              <div className="flex min-w-0 items-center gap-2 md:hidden">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  aria-label="رجوع"
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-foreground active:bg-muted"
                >
                  <ArrowRight className="h-5 w-5" />
                </button>
                <span className="truncate text-sm font-extrabold">
                  {pageTitles[location.pathname] || (location.pathname.startsWith("/app/shop/") ? "المتجر" : "تفاصيل المنتج")}
                </span>
              </div>
            ) : (
              <div className="flex min-w-0 items-center gap-2 md:hidden">
                <BrandLogo className="h-8 w-[92px] shrink-0 object-contain" />
                {location.pathname === "/app" ? (
                  <LocationPill />
                ) : (
                  <span className="truncate text-sm font-extrabold">{pageTitles[location.pathname]}</span>
                )}
              </div>
            )}
          </div>

          {/* Delivery Location Pill */}
          <div className="hidden min-w-0 flex-1 justify-center sm:justify-start md:flex">
            <LocationPill />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-1 rounded-full bg-brand/[0.055] p-1 md:flex">
            {nav.map((item) => {
              const active = isActive(item);
              return (
                <button
                  key={item.to}
                  data-testid={`topnav-${item.label}`}
                  onClick={() => navigate(item.to)}
                  className={`relative flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-all min-h-[40px] ${
                    active ? "bg-brand text-brand-foreground shadow-sm" : "text-muted-foreground hover:bg-card hover:text-foreground"
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.label}</span>
                  {item.badge > 0 && (
                    <span className={`grid h-[18px] min-w-[18px] place-items-center rounded-full px-1 text-[10px] font-bold ${active ? "bg-white text-brand" : "bg-primary text-primary-foreground"}`}>
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Action / Theme / Profile */}
          <div className="flex shrink-0 items-center gap-2">
            <div className="hidden sm:block">
              <ColorThemeSwitcher />
            </div>
            <button
              data-testid="buyer-profile"
              title="الملف الشخصي"
              aria-label="فتح الملف الشخصي"
              onClick={() => navigate("/app/profile")}
              className={`group relative grid h-10 w-10 shrink-0 place-items-center rounded-full text-xs font-extrabold transition-all min-w-[44px] min-h-[44px] ${
                location.pathname === "/app/profile" ? "bg-primary text-primary-foreground shadow-cta" : "bg-secondary text-foreground"
              }`}
            >
              {userInitial}
              <span className={`absolute -bottom-0.5 -left-0.5 grid h-4 w-4 place-items-center rounded-full border-2 border-background ${location.pathname === "/app/profile" ? "bg-brand text-white" : "bg-card text-muted-foreground group-hover:text-primary"}`}>
                <UserRound className="h-2 w-2" />
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Page Area */}
      <main className={`mx-auto max-w-7xl px-3 sm:px-6 py-3 md:py-8 lg:px-8 flex-1 w-full ${!primaryScreen ? "pb-8" : ""}`}>
        <Outlet />
      </main>

      {primaryScreen && (
        <MobileTabBar items={nav.map((item) => ({
          id: item.exact ? "home" : item.to === "/app/orders" ? "orders" : item.to === "/app/checkout" ? "cart" : "profile",
          label: item.label === "الرئيسية" ? "الرئيسية" : item.label === "طلباتي" ? "طلباتي" : item.label === "السلة" ? "السلة" : "حسابي",
          icon: item.icon,
          badge: item.badge,
        }))} activeId={activeTab} onSelect={navigateTab} label="قائمة المشتري" />
      )}
    </div>
  );
}
